import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { formatDateShort, formatCurrency } from '../../lib/utils';
import { useTheme } from '../../context/ThemeContext';

// ─── Ícones ──────────────────────────────────────────────────────────────────
const IconFilter = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3"/>
  </svg>
);

const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const IconPrint = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

const IconLoader = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ animation: 'spin 1s linear infinite' }}>
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 2a10 10 0 0 1 10 10"/>
  </svg>
);

const IconClipboard = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
  </svg>
);

const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.3-4.3"/>
  </svg>
);

const IconRefresh = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
  </svg>
);

const IconX = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconChevronUp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
);

const IconChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ─── Tipos ───────────────────────────────────────────────────────────────────
interface OrderSummary {
  id: string;
  order_number: string;
  franchisee_name: string;
  franchisee_code: string;
  vehicle_plate: string;
  model: string;
  status: string;
  created_at: string;
  notes: string;
  payment_amount?: number | null;
  payment_method?: string | null;
  payment_status?: string | null;
  payment_due_date?: string | null;
  payment_paid_at?: string | null;
}

type SortField = 'order_number' | 'franchisee_name' | 'vehicle_plate' | 'created_at' | 'status';
type SortDir = 'asc' | 'desc';

let adminFinancialMemoryCache: {
  orders: OrderSummary[];
  franchisees: { id: string; company_name: string; code: string }[];
} | null = null;

// ─── Constantes ──────────────────────────────────────────────────────────────
const STATUS_LABELS: Record<string, string> = {
  solicitado: 'Solicitado',
  em_producao: 'Em Produção',
  enviado: 'Enviado',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  pago: 'Pago',
  vencido: 'Vencido',
  cancelado: 'Cancelado',
};

const escapeHtml = (value: unknown) => String(value ?? '—')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  solicitado:  { bg: '#f0f0f0', text: '#888888', dot: '#c8c8c8' },
  em_producao: { bg: '#f0f0f0', text: '#2e2e2e', dot: '#c8c8c8' },
  enviado:     { bg: '#f0f0f0', text: '#2e2e2e', dot: '#c8c8c8' },
  concluido:   { bg: '#dcfce7', text: '#166534', dot: '#16a34a' },
  cancelado:   { bg: '#f0f0f0', text: '#444333', dot: '#c8c8c8' },
};

const STATUS_COLORS_DARK: Record<string, { bg: string; text: string; dot: string }> = {
  solicitado:  { bg: '#2a2a2a', text: '#c8c8c8', dot: '#c8c8c8' },
  em_producao: { bg: '#252525', text: '#c8c8c8', dot: '#c8c8c8' },
  enviado:     { bg: '#252525', text: '#c8c8c8', dot: '#c8c8c8' },
  concluido:   { bg: '#052e16', text: '#86efac', dot: '#22c55e' },
  cancelado:   { bg: '#2a2a2a', text: '#c8c8c8', dot: '#c8c8c8' },
};

// ─── Componente Principal ────────────────────────────────────────────────────
export default function AdminFinancial() {
  const { theme: currentTheme } = useTheme();
  const isDark = currentTheme === 'dark';
  const financialCacheKey = 'admin-financial:cache-v1';

  const [orders, setOrders] = useState<OrderSummary[]>(() => adminFinancialMemoryCache?.orders || []);
  const [loading, setLoading] = useState(() => !adminFinancialMemoryCache);
  const [refreshing, setRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filterFranchisee, setFilterFranchisee] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [franchisees, setFranchisees] = useState<{ id: string; company_name: string; code: string }[]>(
    () => adminFinancialMemoryCache?.franchisees || []
  );
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1200);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // ── Cores ─────────────────────────────────────────────────────────────────
  const c = {
    bg:            isDark ? '#0d0d0d' : '#f3f4f6',
    surface:       isDark ? '#141414' : '#ffffff',
    surfaceAlt:    isDark ? '#0f0f0f' : '#f9fafb',
    surfaceHover:  isDark ? '#1c1c1c' : '#f0f9ff',
    text:          isDark ? '#e5e5e5' : '#1a1a1a',
    textSec:       isDark ? '#888' : '#777777',
    textMuted:     isDark ? '#555' : '#9ca3af',
    border:        isDark ? '#222' : '#e5e7eb',
    borderFocus:   isDark ? '#444' : '#d1d5db',
    accent:        '#c8c8c8',
    accentBg:      isDark ? '#1e1e1e' : '#f0f0f0',
  };

  const statusStyle = (status: string) => {
    const map = isDark ? STATUS_COLORS_DARK : STATUS_COLORS;
    return map[status] ?? { bg: c.surface, text: c.text, dot: c.textMuted };
  };

  // ── Dados ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (adminFinancialMemoryCache) return;
    const cached = sessionStorage.getItem(financialCacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as {
          orders?: OrderSummary[];
          franchisees?: { id: string; company_name: string; code: string }[];
        };

        if (parsed.orders?.length) setOrders(parsed.orders);
        if (parsed.franchisees?.length) setFranchisees(parsed.franchisees);
        setLoading(false);
      } catch {
        loadData();
      }
    } else {
      loadData();
    }
  }, []);

  // Mantém a lista correta quando outro usuário cria/edita pedidos ou pagamentos.
  // O pequeno atraso agrupa várias alterações de uma mesma operação em uma única busca.
  useEffect(() => {
    let refreshTimer: number | undefined;
    const scheduleRefresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => loadData(true), 350);
    };

    const channel = supabase
      .channel('admin-financial-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'financial_records' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'franchisees' }, scheduleRefresh)
      .subscribe();

    return () => {
      window.clearTimeout(refreshTimer);
      supabase.removeChannel(channel);
    };
  }, []);

  async function loadData(silent = false) {
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const [{ data: fData, error: fErr }, { data: oData, error: oErr }] = await Promise.all([
        supabase.from('franchisees').select('id, company_name, code').order('company_name'),
        supabase.from('orders').select(`
          id, order_number, vehicle_plate, model, status, created_at, notes,
          franchisee:franchisees(company_name, code)
        `).order('created_at', { ascending: false }),
      ]);

      if (fErr) throw fErr;
      if (oErr) throw oErr;

      setFranchisees(fData || []);
      // Buscar pagamentos associados aos pedidos retornados
      const orderIds = (oData || []).map((o: any) => o.id).filter(Boolean);
      let paymentsMap: Record<string, any> = {};
      if (orderIds.length) {
        const { data: pData, error: pErr } = await supabase
          .from('financial_records')
          .select('order_id, amount, payment_method, payment_status, due_date, paid_at, created_at')
          .in('order_id', orderIds)
          .order('created_at', { ascending: false });
        if (pErr) throw pErr;
        // keep latest payment per order
        (pData || []).forEach((p: any) => {
          if (!paymentsMap[p.order_id]) paymentsMap[p.order_id] = p;
        });
      }

      const mappedOrders = (oData || []).map(o => ({
        id: o.id,
        order_number: o.order_number ?? '—',
        franchisee_name: (o.franchisee as any)?.company_name ?? '—',
        franchisee_code: (o.franchisee as any)?.code ?? '—',
        vehicle_plate: o.vehicle_plate ?? '—',
        model: o.model ?? '—',
        status: o.status,
        created_at: o.created_at,
        notes: o.notes ?? '',
        payment_amount: paymentsMap[o.id]?.amount ?? null,
        payment_method: paymentsMap[o.id]?.payment_method ?? null,
        payment_status: paymentsMap[o.id]?.payment_status ?? null,
        payment_due_date: paymentsMap[o.id]?.due_date ?? null,
        payment_paid_at: paymentsMap[o.id]?.paid_at ?? null,
      }));

      setOrders(mappedOrders);
      adminFinancialMemoryCache = { orders: mappedOrders, franchisees: fData || [] };

      sessionStorage.setItem(
        financialCacheKey,
        JSON.stringify({
          orders: mappedOrders,
          franchisees: fData || [],
        })
      );
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  // ── Filtro + Ordenação ────────────────────────────────────────────────────
  const hasFilters = !!(filterFranchisee || filterStatus || filterPaymentStatus || dateRange.start || dateRange.end || searchTerm);

  const filteredOrders = useMemo(() => {
    return orders
      .filter(o => {
        if (filterFranchisee && o.franchisee_name !== filterFranchisee) return false;
        if (filterStatus && o.status !== filterStatus) return false;
        if (filterPaymentStatus === 'pendente') {
          const hasPaymentAmount = o.payment_amount != null && Number(o.payment_amount) > 0;
          if (o.payment_status !== 'pendente' && hasPaymentAmount) return false;
        } else if (filterPaymentStatus && o.payment_status !== filterPaymentStatus) return false;
        if (dateRange.start && new Date(o.created_at) < new Date(dateRange.start)) return false;
        if (dateRange.end && new Date(o.created_at) > new Date(dateRange.end + 'T23:59:59')) return false;
        if (searchTerm) {
          const s = searchTerm.toLowerCase();
          if (
            !(o.order_number ?? '').toLowerCase().includes(s) &&
            !(o.vehicle_plate ?? '').toLowerCase().includes(s) &&
            !(o.franchisee_name ?? '').toLowerCase().includes(s) &&
            !(o.model ?? '').toLowerCase().includes(s)
          ) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const va = (a as any)[sortField];
        const vb = (b as any)[sortField];
        if (sortField === 'created_at') {
          return sortDir === 'desc'
            ? new Date(vb).getTime() - new Date(va).getTime()
            : new Date(va).getTime() - new Date(vb).getTime();
        }
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      });
  }, [orders, filterFranchisee, filterStatus, filterPaymentStatus, dateRange.start, dateRange.end, searchTerm, sortField, sortDir]);

  const visibleOrders = filteredOrders.slice(0, visibleCount);

  useEffect(() => setVisibleCount(10), [filterFranchisee, filterStatus, filterPaymentStatus, dateRange.start, dateRange.end, searchTerm, sortField, sortDir]);

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  // ── Estatísticas ──────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:       orders.length,
    solicitado:  orders.filter(o => o.status === 'solicitado').length,
    em_producao: orders.filter(o => o.status === 'em_producao').length,
    enviado:     orders.filter(o => o.status === 'enviado').length,
    concluido:   orders.filter(o => o.status === 'concluido').length,
    cancelado:   orders.filter(o => o.status === 'cancelado').length,
  }), [orders]);

  // ── Export ────────────────────────────────────────────────────────────────
  function exportCSV() {
    const header = 'Pedido,Franqueado,Codigo,Placa,Modelo,Status,Data,Observacoes\n';
    const rows = filteredOrders.map(o =>
      [o.order_number, `"${o.franchisee_name}"`, o.franchisee_code, o.vehicle_plate,
       `"${o.model}"`, STATUS_LABELS[o.status] ?? o.status,
       formatDateShort(o.created_at), `"${o.notes.replace(/"/g, '""')}"`].join(',')
    ).join('\n');
    const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pedidos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function printOrderReceipt(order: OrderSummary) {
    const receiptWindow = window.open('', '_blank', 'width=800,height=900');
    if (!receiptWindow) {
      window.alert('Não foi possível abrir a impressão. Verifique se o navegador bloqueou a janela.');
      return;
    }

    const paymentStatus = order.payment_status
      ? (PAYMENT_STATUS_LABELS[order.payment_status] ?? order.payment_status)
      : 'Não informado';
    const paymentAmount = order.payment_amount != null ? formatCurrency(order.payment_amount) : 'Não informado';
    const dueDate = order.payment_due_date ? formatDateShort(order.payment_due_date) : 'Não informado';
    const paidAt = order.payment_paid_at ? formatDateShort(order.payment_paid_at) : 'Não informado';

    receiptWindow.document.write(`<!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <title>Recibo - ${escapeHtml(order.order_number)}</title>
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; padding: 32px; color: #1f2937; font: 14px Arial, sans-serif; }
            .header { border-bottom: 2px solid #111827; padding-bottom: 16px; margin-bottom: 24px; }
            h1 { margin: 0; font-size: 24px; } .sub { margin-top: 6px; color: #4b5563; }
            h2 { margin: 24px 0 10px; font-size: 15px; text-transform: uppercase; letter-spacing: .05em; }
            .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); border: 1px solid #d1d5db; border-radius: 6px; overflow: hidden; }
            .field { padding: 11px 12px; border-bottom: 1px solid #e5e7eb; } .field:nth-last-child(-n+2) { border-bottom: 0; }
            .label { display: block; margin-bottom: 4px; font-size: 11px; color: #6b7280; text-transform: uppercase; }
            .value { word-break: break-word; } .payment { background: #f3f4f6; border: 2px solid #111827; }
            .payment .label, .payment .value { font-weight: 700; color: #111827; }
            .notes { white-space: pre-wrap; line-height: 1.5; border: 1px solid #d1d5db; border-radius: 6px; padding: 12px; min-height: 56px; }
            .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #d1d5db; color: #6b7280; font-size: 11px; }
            @media print { body { padding: 18px; } }
          </style>
        </head>
        <body>
          <div class="header"><h1>Extrato / Recibo de Pedido</h1><div class="sub">Pedido ${escapeHtml(order.order_number)} · Emitido em ${escapeHtml(new Date().toLocaleString('pt-BR'))}</div></div>
          <h2>Informações do pedido</h2>
          <div class="grid">
            <div class="field"><span class="label">Pedido</span><span class="value">${escapeHtml(order.order_number)}</span></div>
            <div class="field"><span class="label">Status</span><span class="value">${escapeHtml(STATUS_LABELS[order.status] ?? order.status)}</span></div>
            <div class="field"><span class="label">Franqueado</span><span class="value">${escapeHtml(order.franchisee_name)}</span></div>
            <div class="field"><span class="label">Código do franqueado</span><span class="value">${escapeHtml(order.franchisee_code)}</span></div>
            <div class="field"><span class="label">Veículo / placa</span><span class="value">${escapeHtml(order.vehicle_plate)}</span></div>
            <div class="field"><span class="label">Modelo</span><span class="value">${escapeHtml(order.model)}</span></div>
            <div class="field"><span class="label">Data do pedido</span><span class="value">${escapeHtml(formatDateShort(order.created_at))}</span></div>
            <div class="field"><span class="label">Identificador</span><span class="value">${escapeHtml(order.id)}</span></div>
          </div>
          <h2>Pagamento</h2>
          <div class="grid payment">
            <div class="field"><span class="label">Valor</span><span class="value">${escapeHtml(paymentAmount)}</span></div>
            <div class="field"><span class="label">Situação</span><span class="value">${escapeHtml(paymentStatus)}</span></div>
            <div class="field"><span class="label">Forma de pagamento</span><span class="value">${escapeHtml(order.payment_method || 'Não informado')}</span></div>
            <div class="field"><span class="label">Vencimento</span><span class="value">${escapeHtml(dueDate)}</span></div>
            <div class="field"><span class="label">Data do pagamento</span><span class="value">${escapeHtml(paidAt)}</span></div>
          </div>
          <h2>Observações</h2><div class="notes">${escapeHtml(order.notes || 'Sem observações registradas.')}</div>
          <div class="footer">Documento gerado pelo Portal ETORK.</div>
        </body>
      </html>`);
    receiptWindow.document.close();
    receiptWindow.focus();
    window.setTimeout(() => receiptWindow.print(), 250);
  }

  // ── Estilos reutilizáveis ─────────────────────────────────────────────────
  const inputSt: React.CSSProperties = {
    padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${c.border}`, background: c.bg,
    color: c.text, fontSize: 12, outline: 'none', width: '100%',
  };

  const thSt = (field?: SortField): React.CSSProperties => ({
    padding: '10px 14px', textAlign: 'left', fontSize: 10,
    fontWeight: 700, color: c.textMuted, letterSpacing: 0.8,
    cursor: field ? 'pointer' : 'default',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    background: c.surfaceAlt,
  });

  const SortIcon = ({ field }: { field: SortField }) =>
    sortField === field
      ? (sortDir === 'asc' ? <IconChevronUp /> : <IconChevronDown />)
      : <span style={{ opacity: 0.3 }}><IconChevronDown /></span>;

  const spinKeyframes = `
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  `;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: c.bg, minHeight: '100vh' }}>
      <style>{spinKeyframes}</style>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: isMobile ? 12 : 24, animation: 'fadeIn 0.3s ease' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? 18 : 28, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ color: c.text, fontSize: isMobile ? 20 : 24, fontWeight: 800, margin: '0 0 4px' }}>
              Extrato de Pedidos
            </h1>
            <p style={{ color: c.textSec, fontSize: 13, margin: 0 }}>
              Todos os pedidos realizados pelos franqueados
            </p>
          </div>
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', background: c.surface,
              border: `1px solid ${c.border}`, borderRadius: 8,
              color: c.textSec, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            <IconRefresh /> {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'TOTAL', value: stats.total, color: c.accent, borderColor: c.accent },
            { label: 'SOLICITADOS', value: stats.solicitado, color: '#c8c8c8', borderColor: '#c8c8c8' },
            { label: 'EM PRODUÇÃO', value: stats.em_producao, color: '#c8c8c8', borderColor: '#c8c8c8' },
            { label: 'ENVIADOS', value: stats.enviado, color: '#c8c8c8', borderColor: '#c8c8c8' },
            { label: 'CONCLUÍDOS', value: stats.concluido, color: '#16a34a', borderColor: '#16a34a' },
            { label: 'CANCELADOS', value: stats.cancelado, color: '#c8c8c8', borderColor: '#c8c8c8' },
          ].map(({ label, value, color, borderColor }) => (
            <div key={label} style={{
              background: c.surface, border: `1px solid ${c.border}`,
              borderTop: `3px solid ${borderColor}`, borderRadius: 10,
              padding: '14px 16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Barra de ferramentas */}
        <div style={{
          background: c.surface, border: `1px solid ${c.border}`,
          borderRadius: 12, padding: isMobile ? '12px 12px' : '14px 18px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            {/* Esquerda */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
              {/* Busca */}
              <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: c.textMuted }}>
                  <IconSearch />
                </span>
                <input
                  type="text"
                  placeholder="Buscar pedido, placa, franqueado..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{ ...inputSt, paddingLeft: 32 }}
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: c.textMuted }}>
                    <IconX />
                  </button>
                )}
              </div>

              {/* Toggle filtros */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  border: `1px solid ${showFilters ? c.accent : c.border}`,
                  background: showFilters ? c.accentBg : 'transparent',
                  color: showFilters ? c.accent : c.textSec,
                  cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                }}
              >
                <IconFilter />
                Filtros
                {hasFilters && (
                  <span style={{
                    background: c.accent, color: '#000', borderRadius: '50%',
                    width: 16, height: 16, fontSize: 10, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {[filterFranchisee, filterStatus, filterPaymentStatus, dateRange.start, dateRange.end].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>

            {/* Direita */}
            <button
              onClick={exportCSV}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: isDark ? '#1c1c1c' : '#f0f0f0',
                border: `1px solid ${c.accent}`,
                borderRadius: 8, padding: '8px 16px',
                color: c.accent, cursor: 'pointer', fontSize: 12, fontWeight: 700,
              }}
            >
              <IconDownload /> Exportar CSV
            </button>
          </div>

          {/* Painel de filtros expandido */}
          {showFilters && (
            <div style={{
              marginTop: 14, paddingTop: 14, borderTop: `1px solid ${c.border}`,
              display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10,
            }}>
              <select value={filterFranchisee} onChange={e => setFilterFranchisee(e.target.value)} style={inputSt}>
                <option value="">Todos os franqueados</option>
                {franchisees.map(f => <option key={f.id} value={f.company_name}>{f.company_name}</option>)}
              </select>

              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={inputSt}>
                <option value="">Todos os status</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>

              <select value={filterPaymentStatus} onChange={e => setFilterPaymentStatus(e.target.value)} style={inputSt}>
                <option value="">Todos os pagamentos</option>
                {Object.entries(PAYMENT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 10, color: c.textMuted, fontWeight: 700 }}>DATA INICIAL</label>
                <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} style={inputSt} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 10, color: c.textMuted, fontWeight: 700 }}>DATA FINAL</label>
                <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} style={inputSt} />
              </div>

              {hasFilters && (
                <button
                  onClick={() => { setFilterFranchisee(''); setFilterStatus(''); setFilterPaymentStatus(''); setDateRange({ start: '', end: '' }); setSearchTerm(''); }}
                  style={{
                    padding: '8px 14px', background: '#c8c8c8', border: 'none',
                    borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                >
                  <IconX /> Limpar tudo
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tabela */}
        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden' }}>

          {/* Cabeçalho da tabela */}
          <div style={{
            padding: isMobile ? '12px 12px' : '14px 18px', borderBottom: `1px solid ${c.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          }}>
            <h2 style={{ color: c.text, fontSize: 14, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <IconClipboard /> Lista de Pedidos
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {hasFilters && (
                <span style={{
                  fontSize: 11, color: c.accent, background: c.accentBg,
                  padding: '3px 8px', borderRadius: 20, fontWeight: 600,
                }}>
                  Filtros ativos
                </span>
              )}
              <span style={{ fontSize: 12, color: c.textMuted }}>
                {filteredOrders.length} / {orders.length} pedidos
              </span>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 64, textAlign: 'center', color: c.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 13 }}>
              <IconLoader /> Carregando pedidos...
            </div>
          ) : filteredOrders.length === 0 ? (
            <div style={{ padding: 64, textAlign: 'center', color: c.textMuted, fontSize: 13 }}>
              Nenhum pedido encontrado{hasFilters ? ' com os filtros aplicados.' : '.'}
            </div>
          ) : isMobile ? (
            <div style={{ padding: 10, display: 'grid', gap: 8 }}>
              {visibleOrders.map((order) => {
                const st = statusStyle(order.status);
                const isExpanded = expandedId === order.id;

                return (
                  <div key={order.id} style={{ border: `1px solid ${c.border}`, borderRadius: 8, overflow: 'hidden' }}>
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : order.id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        background: isExpanded ? c.surfaceHover : 'transparent',
                        border: 'none',
                        padding: 10,
                        color: c.text,
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 700, color: c.accent }}>{order.order_number}</div>
                      <div style={{ fontSize: 11, marginTop: 3 }}>{order.franchisee_name} · {order.franchisee_code}</div>
                      <div style={{ fontSize: 11, color: c.textSec, marginTop: 2 }}>{order.vehicle_plate} · {formatDateShort(order.created_at)}</div>
                      <div style={{ marginTop: 8 }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: st.bg, color: st.text,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, flexShrink: 0 }} />
                          {STATUS_LABELS[order.status] ?? order.status}
                        </span>
                      </div>
                    </button>

                    <div style={{ borderTop: `1px solid ${c.border}`, padding: '8px 10px', display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => printOrderReceipt(order)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${c.border}`, background: c.surface, color: c.text, borderRadius: 6, padding: '7px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                      >
                        <IconPrint /> Imprimir recibo
                      </button>
                    </div>

                    {isExpanded && (
                      <div style={{ borderTop: `1px solid ${c.border}`, background: c.surfaceHover, padding: 10 }}>
                        <div style={{ fontSize: 11, color: c.textMuted, fontWeight: 700, marginBottom: 6 }}>MODELO</div>
                        <div style={{ fontSize: 12, color: c.text, marginBottom: 10 }}>{order.model}</div>
                        <div style={{ fontSize: 11, color: c.textMuted, fontWeight: 700, marginBottom: 6 }}>OBSERVAÇÕES</div>
                        <div style={{ fontSize: 12, color: c.text }}>{order.notes || 'Sem observações registradas.'}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ overflowX: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${c.border}` }}>
                    <th style={{ ...thSt('order_number'), width: '12%', paddingRight: 28 }} onClick={() => toggleSort('order_number')}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>PEDIDO <SortIcon field="order_number" /></span>
                    </th>
                    <th style={{ ...thSt('franchisee_name'), width: '18%', paddingLeft: 28, borderLeft: `1px solid ${c.border}` }} onClick={() => toggleSort('franchisee_name')}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>FRANQUEADO <SortIcon field="franchisee_name" /></span>
                    </th>
                    <th style={thSt('vehicle_plate')} onClick={() => toggleSort('vehicle_plate')}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>PLACA <SortIcon field="vehicle_plate" /></span>
                    </th>
                    <th style={thSt()}>MODELO</th>
                    <th style={thSt('created_at')} onClick={() => toggleSort('created_at')}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>DATA <SortIcon field="created_at" /></span>
                    </th>
                    <th style={thSt('status')} onClick={() => toggleSort('status')}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>STATUS <SortIcon field="status" /></span>
                    </th>
                    <th style={thSt()}>PAGAMENTO</th>
                    <th style={thSt()}>OBSERVAÇÕES</th>
                    <th style={thSt()}>AÇÃO</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((order, idx) => {
                    const st = statusStyle(order.status);
                    const isExpanded = expandedId === order.id;
                    const isLast = idx === filteredOrders.length - 1;

                    return (
                      <React.Fragment key={order.id}>
                        <tr
                          onClick={() => setExpandedId(isExpanded ? null : order.id)}
                          style={{
                            borderBottom: isLast && !isExpanded ? 'none' : `1px solid ${c.border}`,
                            cursor: 'pointer', transition: 'background 0.12s',
                            background: isExpanded ? c.surfaceHover : 'transparent',
                          }}
                          onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = c.surfaceAlt; }}
                          onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <td style={{ padding: '11px 28px 11px 14px', fontSize: 13, fontWeight: 700, color: c.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {order.order_number}
                          </td>
                          <td style={{ padding: '11px 14px 11px 28px', borderLeft: `1px solid ${c.border}`, overflow: 'hidden' }}>
                            <div style={{ fontSize: 13, color: c.text, fontWeight: 600, overflowWrap: 'anywhere' }}>{order.franchisee_name}</div>
                            <div style={{ fontSize: 10, color: c.textMuted, marginTop: 2 }}>{order.franchisee_code}</div>
                          </td>
                          <td style={{ padding: '11px 14px', fontSize: 12, color: c.textSec, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                            {order.vehicle_plate}
                          </td>
                          <td style={{ padding: '11px 14px', fontSize: 12, color: c.textSec, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {order.model}
                          </td>
                          <td style={{ padding: '11px 14px', fontSize: 12, color: c.textSec, whiteSpace: 'nowrap' }}>
                            {formatDateShort(order.created_at)}
                          </td>
                          <td style={{ padding: '11px 14px' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                              background: st.bg, color: st.text,
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, flexShrink: 0 }} />
                              {STATUS_LABELS[order.status] ?? order.status}
                            </span>
                          </td>
                          <td style={{ padding: '11px 14px', fontSize: 12, color: c.textSec, whiteSpace: 'nowrap' }}>
                            {order.payment_amount ? (
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontWeight: 700 }}>{formatCurrency(order.payment_amount)}</div>
                                <div style={{ fontSize: 11, color: c.textMuted }}>{order.payment_status ?? '—'}</div>
                              </div>
                            ) : (
                              <div style={{ color: c.textMuted }}>—</div>
                            )}
                          </td>
                          <td style={{ padding: '11px 14px', fontSize: 11, color: c.textSec, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {order.notes || '—'}
                          </td>
                          <td style={{ padding: '11px 14px' }}>
                            <button
                              type="button"
                              aria-label={`Imprimir recibo do pedido ${order.order_number}`}
                              title="Imprimir extrato / recibo"
                              onClick={(event) => { event.stopPropagation(); printOrderReceipt(order); }}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5, border: `1px solid ${c.border}`,
                                background: c.surface, color: c.text, borderRadius: 6, padding: '6px 9px',
                                fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                              }}
                            >
                              <IconPrint /> Imprimir
                            </button>
                          </td>
                        </tr>

                        {/* Linha expandida com notas completas */}
                        {isExpanded && (
                          <tr key={`${order.id}-expanded`} style={{ borderBottom: isLast ? 'none' : `1px solid ${c.border}` }}>
                            <td colSpan={9} style={{ padding: '12px 20px 16px 20px', background: c.surfaceHover }}>
                              <div style={{ fontSize: 11, color: c.textMuted, fontWeight: 700, marginBottom: 6 }}>OBSERVAÇÕES COMPLETAS</div>
                              <div style={{ fontSize: 12, color: c.text, lineHeight: 1.6, whiteSpace: 'pre-wrap', maxWidth: 900 }}>
                                {order.notes || 'Sem observações registradas.'}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && visibleCount < filteredOrders.length && (
            <div style={{ padding: '12px 18px', borderTop: `1px solid ${c.border}`, textAlign: 'center' }}>
              <button type="button" onClick={() => setVisibleCount((count) => count + 10)}
                style={{ padding: '8px 18px', borderRadius: 7, border: `1px solid ${c.border}`, background: c.surfaceAlt, color: c.text, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                Ver mais ({filteredOrders.length - visibleCount} restantes)
              </button>
            </div>
          )}

          {/* Rodapé */}
          {!loading && filteredOrders.length > 0 && (
            <div style={{
              padding: isMobile ? '10px 12px' : '12px 18px', borderTop: `1px solid ${c.border}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: 11, color: c.textMuted,
            }}>
              <span>{isMobile ? 'Toque em um card para ver detalhes' : 'Clique em uma linha para ver as observações completas'}</span>
              <span>{filteredOrders.length} resultado(s)</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
